import React from 'react';
import { useTranslation } from 'react-i18next';
import useClasses from '../../hooks/useClasses';

import CoreLink from '../CoreLink';
import SkeletonWrapper from '../SkeletonWrapper';

import './style.scss';

interface Client {
  name: string
  uid: number | string
  domain: string
  weeks_registered: number
}

interface Props {
  clients: Client[]
  isError?: boolean
  isLoading?: boolean
}

// TODO: Link to client page
const ClientsOverview: React.FC<Props> = ({
  clients,
  isError,
  isLoading,
}) => {
  const { buildClass } = useClasses('ClientsOverview', {
    'is-loading': isLoading,
    'is-error': isError,
  });
  const { t } = useTranslation();

  if (isError) {
    return (
      <div className={buildClass('zero-state')}>
        <p className="body--large">
          {t('pages.clients.error')}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={buildClass()}>
        {[...Array(5).keys()].map((item) => (
          <SkeletonWrapper
            active={isLoading}
            key={`clients-list-loader-${item}`}
            className={buildClass('skeleton')}
          />
        ))}
      </div>
    );
  }

  if (!clients.length) {
    return (
      <div className={buildClass('zero-state')}>
        <p className="body--large">
          {t('pages.clients.no_clients')}
        </p>
      </div>
    );
  }

  return (
    <table className={buildClass()}>
      <thead className={buildClass('head', 'normal')}>
        <tr>
          <th scope="col">
            {t('pages.clients.name')}
          </th>
          <th scope="col">
            {t('pages.clients.domain')}
          </th>
          <th scope="col">
            {t('pages.clients.current_week')}
          </th>
        </tr>
      </thead>
      <tbody className={buildClass('body', 'normal')}>
        {clients.map((client) => (
          <tr key={`clients-overview-item-normal-${client.uid}`}>
            <th>
              <CoreLink to={`/client/${client.domain}/${client.uid}`}>
                {client.name}
              </CoreLink>
            </th>
            <td>
              {t(`pages.clients.domains.${client.domain}`)}
            </td>
            <td>
              {t('pages.clients.week', {
                week: client.weeks_registered,
              })}
            </td>
          </tr>
        ))}
      </tbody>
      {clients.map((client) => (
        <React.Fragment key={`clients-overview-item-small-${client.uid}`}>
          <thead className={buildClass('head', 'small')}>
            <tr>
              <th scope="col">
                <CoreLink to={`/client/${client.domain}/${client.uid}`}>
                  {client.name}
                </CoreLink>
              </th>
            </tr>
          </thead>
          <tbody className={buildClass('body', 'small')}>
            <tr>
              <th scope="row" className={buildClass('item-header')}>
                {t('pages.clients.domain')}
              </th>
              <td>
                {t(`pages.clients.domains.${client.domain}`)}
              </td>
            </tr>
            <tr>
              <th scope="row" className={buildClass('item-header')}>
                {t('pages.clients.current_week')}
              </th>
              <td>
                {client.weeks_registered}
              </td>
            </tr>
          </tbody>
        </React.Fragment>
      ))}
    </table>
  );
};

export default ClientsOverview;
